import { acceptHMRUpdate, defineStore } from 'pinia'
import { api } from '@/api/client'
import { useConsoleStore } from './console'
import { num } from '@/utils/format'

const STORAGE_KEY = 'nexus.planLocal'

/**
 * 盘前计划。
 *
 * **计划本身由后端每日生成并持有**，这个 store 只是它的读侧视图 ——
 * 档位、参考价、风险线、批准状态全部来自快照的 plan 块，前端不再自己算。
 *
 * 这么改的原因：计划是当日交易的授权凭据。放在 localStorage 里意味着
 * 换台机器就没了、两个人看到的档位可能不一样、谁批的什么时候批的也查不到。
 *
 * 仍留在本地的只有「风控演示」—— 那是演练用的模拟输入，
 * 不是真实授权，不该写进后端的计划记录里。
 */
function localDefaults() {
  return { demo: { on: false, pnl: 0 } }
}

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? { ...localDefaults(), ...JSON.parse(raw) } : localDefaults()
  } catch {
    return localDefaults()
  }
}

/** 空计划：后端还没生成时用它兜住模板，避免每处都写 ?. 判空 */
const EMPTY = {
  date: '—',
  ladders: [],
  checks: [],
  exit_tiers: [],
  limits: {},
  approved: false,
  expired: false,
  usable: false,
  pending: true,
}

export const usePlanStore = defineStore('plan', {
  state: () => ({
    ...loadLocal(),
    busy: false,
  }),

  getters: {
    /** 后端计划原文。快照里没有就退回空模板。 */
    plan() {
      return useConsoleStore().snap?.plan ?? EMPTY
    },

    pending() {
      return !!this.plan.pending
    },
    pendingReason() {
      return this.plan.pending_reason || ''
    },

    date() {
      return this.plan.date ?? '—'
    },
    generatedAt() {
      return this.plan.generated_at ?? 0
    },
    expiryTs() {
      return this.plan.expires_at ?? 0
    },
    expired() {
      return !!this.plan.expired
    },
    approved() {
      return !!this.plan.approved
    },
    approvedAt() {
      return this.plan.approved_at ?? 0
    },
    approvedBy() {
      return this.plan.approved_by ?? ''
    },
    revokedReason() {
      return this.plan.revoked_reason || ''
    },

    refPrice() {
      return num(this.plan.ref_price) ?? 0
    },
    maxInvest() {
      return num(this.plan.max_invest) ?? 0
    },
    minNetProfit() {
      return num(this.plan.min_net_profit) ?? 0
    },
    trailingDrawdown() {
      return num(this.plan.trailing_drawdown) ?? 0
    },

    ladders() {
      return (this.plan.ladders ?? []).map((l) => ({
        level: l.level,
        price: num(l.price),
        amount: num(l.amount),
        type: l.type,
      }))
    },
    activeLadders() {
      return this.ladders.filter((l) => l.price)
    },
    exitTiers() {
      return (this.plan.exit_tiers ?? []).map((t) => ({
        key: t.key,
        name: t.name,
        pnl: num(t.target_net) ?? 0,
        ratio: num(t.ratio) ?? 0,
        note: t.note,
      }))
    },

    limits() {
      const l = this.plan.limits ?? {}
      return {
        observe: num(l.observe) ?? 0,
        pause: num(l.pause) ?? 0,
        stop: num(l.stop) ?? 0,
        floorPrice: num(l.floor_price) ?? 0,
      }
    },

    // 开仓条件（规则书第三章 No Trade）。文案来自后端，与规则同源。
    noTrade() {
      return this.plan.checks ?? []
    },
    noTradeAllPass() {
      return !!this.plan.all_checks_passed
    },
    noTradeFailedCount() {
      return (this.plan.checks_total ?? 0) - (this.plan.checks_passed ?? 0)
    },

    /** 风控引擎唯一该看的字段，由后端判定：已批准 + 未过期 + 条件全通过 */
    usable() {
      return !!this.plan.usable
    },
  },

  actions: {
    persistLocal() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ demo: this.demo }))
      } catch {
        /* 隐私模式忽略 */
      }
    },

    /** 所有写操作走同一条路：调后端 → 立刻重拉快照，不在前端猜结果 */
    async _act(fn) {
      this.busy = true
      try {
        await fn()
        await useConsoleStore().pull()
      } finally {
        this.busy = false
      }
    },

    toggleNoTrade(index) {
      const item = this.noTrade[index]
      return this._act(() => api.planCheck(index, !item?.pass))
    },

    approve(operator) {
      return this._act(() => api.planApprove(operator))
    },

    revoke(reason) {
      return this._act(() => api.planRevoke(reason))
    },

    saveLimits(limits) {
      return this._act(() =>
        api.planLimits({
          observe: limits.observe,
          pause: limits.pause,
          stop: limits.stop,
          floor_price: limits.floorPrice,
        }),
      )
    },

    regenerate() {
      return this._act(() => api.planRegenerate())
    },

    setDemo(on, pnl) {
      this.demo.on = on
      if (pnl !== undefined) this.demo.pnl = pnl
      this.persistLocal()
    },
  },
})

/* 让 store 支持热更新，理由同 risk.js：
   缺这一行时改 store 只有整页刷新才生效，容易误判成改动没起作用。 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePlanStore, import.meta.hot))
}
